import { useState } from "react";
import { useTranslation } from "react-i18next";
import { PageLayout } from "@/components/PageLayout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { useNavigate } from "react-router-dom";
import { useUserRole } from "@/hooks/useUserRole";
import { useAuth } from "@/contexts/AuthContext";
import { useLatexProgress } from "@/hooks/useLatexProgress";
import { Lock, BookOpen, GraduationCap, Sparkles, ChevronRight, Code2, FileText, Copy, Check, Trophy } from "lucide-react";
import { Math } from "@/components/Math";
import { toast } from "sonner";
import { SEO } from "@/components/SEO";
import { InteractiveExercise } from "@/components/learn/InteractiveExercise";
import { ProgressIndicator } from "@/components/learn/ProgressIndicator";
import { FinalProject } from "@/components/learn/FinalProject";

interface Lesson {
  id: string;
  title: string;
  description: string;
  content: string;
  examples: { latex: string; description: string }[];
  exercises: { prompt: string; answer: string }[];
  isFree?: boolean;
}

const beginnerLessons: Lesson[] = [
  {
    id: "basics",
    title: "Introduction to LaTeX",
    description: "Learn the fundamentals of LaTeX syntax and basic equations",
    isFree: true,
    content: `LaTeX is a typesetting system widely used for mathematical and scientific documents. It allows you to write complex equations in a readable format.

**Basic Syntax:**
- Inline math: Wrap with single dollar signs \`$...$\`
- Display math: Wrap with double dollar signs \`$$...$$\`
- Backslash commands: \`\\frac{}{}\`, \`\\sqrt{}\`, etc.
- Curly braces group content: \`{...}\``,
    examples: [
      { latex: "x^2 + y^2 = z^2", description: "Superscripts using ^" },
      { latex: "a_1 + a_2 = a_3", description: "Subscripts using _" },
      { latex: "\\frac{a}{b}", description: "Fractions with \\frac{}{}" },
      { latex: "\\sqrt{x}", description: "Square root with \\sqrt{}" },
      { latex: "\\sqrt[3]{x}", description: "Cube root with \\sqrt[3]{}" },
      { latex: "x^{10} + y^{20}", description: "Multi-digit exponents need braces" },
    ],
    exercises: [
      { prompt: "Write x squared plus y cubed", answer: "x^2 + y^3" },
      { prompt: "Write a fraction: 3 over 4", answer: "\\frac{3}{4}" },
      { prompt: "Write the square root of 16", answer: "\\sqrt{16}" },
      { prompt: "Write x to the power of 12", answer: "x^{12}" },
      { prompt: "Write a subscript: variable a sub n", answer: "a_n" },
      { prompt: "Write a fraction inside a square root: sqrt of (a over b)", answer: "\\sqrt{\\frac{a}{b}}" },
    ],
  },
  {
    id: "greek",
    title: "Greek Letters & Symbols",
    description: "Master Greek letters and common mathematical symbols",
    isFree: true,
    content: `Greek letters are essential in mathematics and science. LaTeX provides commands for all Greek letters.

**Common Greek Letters:**
- Lowercase: \\alpha, \\beta, \\gamma, \\delta, \\epsilon, \\theta, \\lambda, \\mu, \\pi, \\sigma, \\omega
- Uppercase: \\Gamma, \\Delta, \\Theta, \\Lambda, \\Pi, \\Sigma, \\Omega

**Mathematical Symbols:**
- Relations: \\leq, \\geq, \\neq, \\approx, \\equiv
- Operations: \\times, \\div, \\pm, \\cdot
- Sets: \\in, \\notin, \\subset, \\cup, \\cap`,
    examples: [
      { latex: "\\alpha + \\beta = \\gamma", description: "Lowercase Greek" },
      { latex: "\\sum_{i=1}^{n} x_i", description: "Summation symbol" },
      { latex: "\\int_0^\\infty e^{-x} dx", description: "Integral symbol" },
      { latex: "\\pi r^2", description: "Pi for area of circle" },
      { latex: "x \\leq y \\leq z", description: "Less than or equal" },
      { latex: "A \\cup B \\cap C", description: "Set operations" },
    ],
    exercises: [
      { prompt: "Write: alpha plus beta equals theta", answer: "\\alpha + \\beta = \\theta" },
      { prompt: "Write pi times r squared", answer: "\\pi r^2" },
      { prompt: "Write: x is less than or equal to 5", answer: "x \\leq 5" },
      { prompt: "Write: a is not equal to b", answer: "a \\neq b" },
      { prompt: "Write the summation of i from 1 to n", answer: "\\sum_{i=1}^{n}" },
      { prompt: "Write: element x belongs to set A", answer: "x \\in A" },
      { prompt: "Write: lambda times mu", answer: "\\lambda \\times \\mu" },
    ],
  },
  {
    id: "document-structure",
    title: "Document Structure",
    description: "Learn to create complete LaTeX documents with sections and paragraphs",
    isFree: true,
    content: `A complete LaTeX document has a preamble and body. The preamble defines the document type and loads packages.

**Document Structure:**
\`\`\`
\\documentclass{article}
\\usepackage{amsmath}
\\title{My Report}
\\author{Your Name}
\\date{\\today}
\\begin{document}
\\maketitle
... content ...
\\end{document}
\`\`\`

**Section Commands:**
- \\section{Title} - Main sections
- \\subsection{Title} - Subsections
- \\subsubsection{Title} - Sub-subsections
- \\paragraph{Title} - Paragraph headings`,
    examples: [
      { latex: "\\section{Introduction}", description: "Main section heading" },
      { latex: "\\subsection{Background}", description: "Subsection" },
      { latex: "\\title{My Research Paper}", description: "Document title" },
      { latex: "\\author{John Smith}", description: "Author name" },
      { latex: "\\date{\\today}", description: "Today's date" },
    ],
    exercises: [
      { prompt: "Create a section called 'Methods'", answer: "\\section{Methods}" },
      { prompt: "Create a subsection called 'Data Collection'", answer: "\\subsection{Data Collection}" },
      { prompt: "Set the document title to 'Annual Report'", answer: "\\title{Annual Report}" },
      { prompt: "Set the author to 'Jane Doe'", answer: "\\author{Jane Doe}" },
      { prompt: "Create a subsubsection called 'Survey Design'", answer: "\\subsubsection{Survey Design}" },
      { prompt: "Use today's date for the document", answer: "\\date{\\today}" },
    ],
  },
];

const intermediateLessons: Lesson[] = [
  {
    id: "text-formatting",
    title: "Text Formatting",
    description: "Bold, italic, underline, and font sizes",
    content: `LaTeX provides many ways to format text for emphasis and clarity.

**Text Styles:**
- \\textbf{bold} - Bold text
- \\textit{italic} - Italic text
- \\underline{underlined} - Underlined text
- \\emph{emphasis} - Contextual emphasis

**Font Sizes (smallest to largest):**
\\tiny, \\scriptsize, \\footnotesize, \\small, \\normalsize, \\large, \\Large, \\LARGE, \\huge, \\Huge

**Text in Math Mode:**
- \\text{words} - Regular text inside equations`,
    examples: [
      { latex: "\\textbf{Important}", description: "Bold text" },
      { latex: "\\textit{emphasized}", description: "Italic text" },
      { latex: "\\underline{underlined}", description: "Underlined text" },
      { latex: "\\textbf{\\textit{bold italic}}", description: "Combined styles" },
      { latex: "E = mc^2 \\text{ (Einstein)}", description: "Text in math mode" },
    ],
    exercises: [
      { prompt: "Make 'Warning' bold", answer: "\\textbf{Warning}" },
      { prompt: "Make 'species name' italic", answer: "\\textit{species name}" },
      { prompt: "Underline 'important'", answer: "\\underline{important}" },
      { prompt: "Make 'Critical' both bold and italic", answer: "\\textbf{\\textit{Critical}}" },
      { prompt: "Add 'where x is velocity' as text in an equation", answer: "\\text{where x is velocity}" },
      { prompt: "Make 'Note:' bold and 'see appendix' italic together", answer: "\\textbf{Note:} \\textit{see appendix}" },
    ],
  },
  {
    id: "lists",
    title: "Lists & Enumerations",
    description: "Create bulleted and numbered lists",
    content: `Lists help organize information clearly. LaTeX supports several list types.

**Bulleted Lists (itemize):**
\`\`\`
\\begin{itemize}
  \\item First item
  \\item Second item
\\end{itemize}
\`\`\`

**Numbered Lists (enumerate):**
\`\`\`
\\begin{enumerate}
  \\item First step
  \\item Second step
\\end{enumerate}
\`\`\`

**Description Lists:**
\`\`\`
\\begin{description}
  \\item[Term] Definition
\\end{description}
\`\`\``,
    examples: [
      { latex: "\\begin{itemize} \\item Apple \\item Banana \\end{itemize}", description: "Bulleted list" },
      { latex: "\\begin{enumerate} \\item First \\item Second \\end{enumerate}", description: "Numbered list" },
    ],
    exercises: [
      { prompt: "Create a single bullet item 'Task completed'", answer: "\\item Task completed" },
      { prompt: "Start a bulleted list environment", answer: "\\begin{itemize}" },
      { prompt: "End a bulleted list environment", answer: "\\end{itemize}" },
      { prompt: "Start a numbered list environment", answer: "\\begin{enumerate}" },
      { prompt: "Create a bullet point for 'Review data'", answer: "\\item Review data" },
    ],
  },
  {
    id: "tables",
    title: "Tables",
    description: "Create professional data tables",
    content: `Tables are essential for presenting data. The tabular environment creates tables.

**Basic Table Structure:**
\`\`\`
\\begin{tabular}{|c|c|c|}
\\hline
Header 1 & Header 2 & Header 3 \\\\
\\hline
Data 1 & Data 2 & Data 3 \\\\
\\hline
\\end{tabular}
\`\`\`

**Column Alignment:**
- l = left aligned
- c = centered
- r = right aligned
- | = vertical line
- \\hline = horizontal line`,
    examples: [
      { latex: "\\begin{tabular}{|c|c|} \\hline A & B \\\\ \\hline 1 & 2 \\\\ \\hline \\end{tabular}", description: "Simple 2-column table" },
      { latex: "\\begin{tabular}{l|r} Name & Score \\\\ \\hline Alice & 95 \\\\ Bob & 87 \\end{tabular}", description: "Left and right aligned" },
    ],
    exercises: [
      { prompt: "Start a table with 3 centered columns with vertical lines", answer: "\\begin{tabular}{|c|c|c|}" },
      { prompt: "Create a horizontal line in a table", answer: "\\hline" },
      { prompt: "Create a table row: Apple & Red", answer: "Apple & Red \\\\" },
      { prompt: "End a tabular environment", answer: "\\end{tabular}" },
      { prompt: "Start a table with left, center, right columns", answer: "\\begin{tabular}{l|c|r}" },
      { prompt: "Create a header row: Name & Age & City with line below", answer: "Name & Age & City \\\\ \\hline" },
    ],
  },
  {
    id: "matrices",
    title: "Matrices & Arrays",
    description: "Create matrices and aligned equations",
    content: `Matrices are essential for linear algebra. LaTeX provides several environments for creating them.

**Matrix Types:**
- \\begin{matrix}...\\end{matrix} - No brackets
- \\begin{pmatrix}...\\end{pmatrix} - Parentheses
- \\begin{bmatrix}...\\end{bmatrix} - Square brackets
- \\begin{vmatrix}...\\end{vmatrix} - Vertical bars (determinant)

**Syntax:** Use & to separate columns, \\\\ for new rows`,
    examples: [
      { latex: "\\begin{pmatrix} a & b \\\\ c & d \\end{pmatrix}", description: "2x2 matrix with parentheses" },
      { latex: "\\begin{bmatrix} 1 & 0 \\\\ 0 & 1 \\end{bmatrix}", description: "Identity matrix" },
      { latex: "\\det(A) = \\begin{vmatrix} a & b \\\\ c & d \\end{vmatrix}", description: "Determinant notation" },
      { latex: "\\begin{bmatrix} 1 \\\\ 2 \\\\ 3 \\end{bmatrix}", description: "Column vector" },
    ],
    exercises: [
      { prompt: "Create a 2x2 identity matrix with square brackets", answer: "\\begin{bmatrix} 1 & 0 \\\\ 0 & 1 \\end{bmatrix}" },
      { prompt: "Create a 2x2 matrix with parentheses containing a,b,c,d", answer: "\\begin{pmatrix} a & b \\\\ c & d \\end{pmatrix}" },
      { prompt: "Start a determinant (vertical bars) matrix", answer: "\\begin{vmatrix}" },
      { prompt: "Create a column vector [1, 2, 3] with square brackets", answer: "\\begin{bmatrix} 1 \\\\ 2 \\\\ 3 \\end{bmatrix}" },
      { prompt: "Create a 3x3 identity matrix", answer: "\\begin{bmatrix} 1 & 0 & 0 \\\\ 0 & 1 & 0 \\\\ 0 & 0 & 1 \\end{bmatrix}" },
    ],
  },
  {
    id: "calculus",
    title: "Calculus Notation",
    description: "Integrals, derivatives, and limits",
    content: `LaTeX excels at typesetting calculus notation with professional formatting.

**Key Commands:**
- Derivatives: \\frac{d}{dx}, \\frac{\\partial}{\\partial x}
- Integrals: \\int, \\iint, \\iiint, \\oint
- Limits: \\lim_{x \\to a}
- Products: \\prod_{i=1}^{n}`,
    examples: [
      { latex: "\\frac{d}{dx}[x^n] = nx^{n-1}", description: "Power rule" },
      { latex: "\\int_a^b f(x)\\,dx", description: "Definite integral" },
      { latex: "\\lim_{x \\to 0} \\frac{\\sin x}{x} = 1", description: "Limit notation" },
      { latex: "\\frac{\\partial f}{\\partial x}", description: "Partial derivative" },
      { latex: "\\iint_R f(x,y)\\,dA", description: "Double integral" },
    ],
    exercises: [
      { prompt: "Write the derivative of x squared equals 2x", answer: "\\frac{d}{dx}[x^2] = 2x" },
      { prompt: "Write an integral from 0 to infinity", answer: "\\int_0^\\infty" },
      { prompt: "Write the limit as x approaches 5", answer: "\\lim_{x \\to 5}" },
      { prompt: "Write a partial derivative with respect to y", answer: "\\frac{\\partial}{\\partial y}" },
      { prompt: "Write a double integral over region R", answer: "\\iint_R" },
      { prompt: "Write the product from i=1 to n", answer: "\\prod_{i=1}^{n}" },
    ],
  },
  {
    id: "equations",
    title: "Multi-line Equations",
    description: "Align equations and create systems",
    content: `For complex derivations, you need multi-line equations with proper alignment.

**Alignment Environments:**
- aligned: For aligned equations within a single equation
- cases: For piecewise functions
- Use & for alignment points and \\\\ for line breaks

**Piecewise Functions:**
\`\`\`
f(x) = \\begin{cases}
  value1 & condition1 \\\\
  value2 & condition2
\\end{cases}
\`\`\``,
    examples: [
      { latex: "\\begin{aligned} x + y &= 5 \\\\ 2x - y &= 1 \\end{aligned}", description: "System of equations" },
      { latex: "f(x) = \\begin{cases} x^2 & x \\geq 0 \\\\ -x^2 & x < 0 \\end{cases}", description: "Piecewise function" },
      { latex: "\\begin{aligned} a &= b + c \\\\ &= d + e + f \\end{aligned}", description: "Multi-step derivation" },
    ],
    exercises: [
      { prompt: "Create a piecewise function: 1 if x>0, 0 if x<=0", answer: "f(x) = \\begin{cases} 1 & x > 0 \\\\ 0 & x \\leq 0 \\end{cases}" },
      { prompt: "Start an aligned environment", answer: "\\begin{aligned}" },
      { prompt: "Create an alignment point with &", answer: "&=" },
      { prompt: "Create a system: x+y=10 and x-y=2", answer: "\\begin{aligned} x + y &= 10 \\\\ x - y &= 2 \\end{aligned}" },
      { prompt: "Start a cases environment for piecewise", answer: "\\begin{cases}" },
    ],
  },
];

const advancedLessons: Lesson[] = [
  {
    id: "physics",
    title: "Physics Notation",
    description: "Vectors, operators, and physics symbols",
    content: `Physics requires specialized notation for vectors, operators, and quantum mechanics.

**Common Physics Commands:**
- Vectors: \\vec{v}, \\mathbf{v}, \\hat{i}
- Operators: \\nabla, \\partial, \\Delta
- Units: Use \\text{} for units
- Brackets: \\langle \\rangle for quantum notation`,
    examples: [
      { latex: "\\vec{F} = m\\vec{a}", description: "Newton's Second Law" },
      { latex: "\\nabla \\cdot \\vec{E} = \\frac{\\rho}{\\epsilon_0}", description: "Gauss's Law" },
      { latex: "E = mc^2", description: "Mass-energy equivalence" },
      { latex: "\\hat{H}\\psi = E\\psi", description: "Schrödinger equation" },
      { latex: "v = 10\\text{ m/s}", description: "With units" },
      { latex: "\\langle \\psi | \\hat{A} | \\psi \\rangle", description: "Bra-ket notation" },
    ],
    exercises: [
      { prompt: "Write F = ma with vectors", answer: "\\vec{F} = m\\vec{a}" },
      { prompt: "Write velocity v = 5 meters per second", answer: "v = 5\\text{ m/s}" },
      { prompt: "Write the nabla (del) operator", answer: "\\nabla" },
      { prompt: "Write a unit vector i-hat", answer: "\\hat{i}" },
      { prompt: "Write E = hf with Planck's constant", answer: "E = h f" },
      { prompt: "Write kinetic energy: KE = (1/2)mv^2 with text label", answer: "KE = \\frac{1}{2}mv^2" },
    ],
  },
  {
    id: "chemistry",
    title: "Chemistry Equations",
    description: "Chemical formulas and reactions",
    content: `Chemistry notation includes subscripts for molecular formulas and arrows for reactions.

**Chemistry Notation:**
- Subscripts for counts: H_2O
- Arrows: \\rightarrow, \\leftrightarrow, \\rightleftharpoons
- Charges: ^{+}, ^{-}, ^{2+}
- Use \\text{} for element symbols`,
    examples: [
      { latex: "\\text{H}_2\\text{O}", description: "Water molecule" },
      { latex: "\\text{2H}_2 + \\text{O}_2 \\rightarrow \\text{2H}_2\\text{O}", description: "Combustion reaction" },
      { latex: "\\text{CH}_3\\text{COOH} \\rightleftharpoons \\text{CH}_3\\text{COO}^- + \\text{H}^+", description: "Acid dissociation" },
      { latex: "\\text{Fe}^{2+} + 2e^- \\rightarrow \\text{Fe}", description: "Reduction reaction" },
    ],
    exercises: [
      { prompt: "Write the formula for glucose C6H12O6", answer: "\\text{C}_6\\text{H}_{12}\\text{O}_6" },
      { prompt: "Write water: H2O", answer: "\\text{H}_2\\text{O}" },
      { prompt: "Write a forward reaction arrow", answer: "\\rightarrow" },
      { prompt: "Write an equilibrium arrow", answer: "\\rightleftharpoons" },
      { prompt: "Write a calcium 2+ ion", answer: "\\text{Ca}^{2+}" },
      { prompt: "Write carbon dioxide: CO2", answer: "\\text{CO}_2" },
    ],
  },
  {
    id: "references",
    title: "References & Citations",
    description: "Cross-references, labels, and footnotes",
    content: `Professional documents need proper referencing and citations.

**Labels and References:**
- \\label{eq:name} - Create a label
- \\ref{eq:name} - Reference a label
- \\eqref{eq:name} - Reference with parentheses

**Footnotes:**
- \\footnote{text} - Creates a footnote

**Equation Numbering:**
Equations in equation environment are auto-numbered`,
    examples: [
      { latex: "\\label{eq:einstein}", description: "Label for an equation" },
      { latex: "\\ref{eq:einstein}", description: "Reference to a label" },
      { latex: "\\eqref{eq:einstein}", description: "Reference with parentheses" },
      { latex: "\\footnote{See appendix for details}", description: "Footnote" },
    ],
    exercises: [
      { prompt: "Create a label called 'eq:main'", answer: "\\label{eq:main}" },
      { prompt: "Reference a label called 'sec:intro'", answer: "\\ref{sec:intro}" },
      { prompt: "Reference an equation with parentheses", answer: "\\eqref{eq:main}" },
      { prompt: "Add a footnote saying 'Data from 2024'", answer: "\\footnote{Data from 2024}" },
      { prompt: "Create a label for a figure called 'fig:graph'", answer: "\\label{fig:graph}" },
    ],
  },
  {
    id: "supercalc",
    title: "SuperCalc Integration",
    description: "Edit and customize SuperCalc AI outputs",
    content: `SuperCalc generates professional LaTeX output that you can customize and export.

**Editing SuperCalc Output:**
1. Copy the LaTeX from SuperCalc results
2. Use the LaTeX Formatter to preview changes
3. Adjust formatting, add explanations, or simplify
4. Export as PNG/JPEG for reports

**Common Edits:**
- Add \\text{} for labels and units
- Use \\boxed{} to highlight final answers
- Add color with \\color{blue}{...}
- Add spacing with \\quad, \\qquad`,
    examples: [
      { latex: "\\boxed{x = \\frac{-b \\pm \\sqrt{b^2-4ac}}{2a}}", description: "Boxed final answer" },
      { latex: "v = \\frac{d}{t} = \\frac{100\\text{ m}}{10\\text{ s}} = 10\\text{ m/s}", description: "With units" },
      { latex: "\\color{blue}{x = 5}", description: "Colored result" },
      { latex: "a \\quad b \\qquad c", description: "Spacing controls" },
    ],
    exercises: [
      { prompt: "Box the answer: x = 5", answer: "\\boxed{x = 5}" },
      { prompt: "Add medium space between a and b", answer: "a \\quad b" },
      { prompt: "Make the number 42 blue", answer: "\\color{blue}{42}" },
      { prompt: "Write '= 25 meters' with proper text formatting", answer: "= 25\\text{ meters}" },
      { prompt: "Box a fraction: 1/2", answer: "\\boxed{\\frac{1}{2}}" },
      { prompt: "Add large space between x and y", answer: "x \\qquad y" },
    ],
  },
];

interface LessonCardProps {
  lesson: Lesson;
  isLocked: boolean;
  isLockedByProgression: boolean;
  onSelect: () => void;
  progressCompleted: number;
  progressTotal: number;
}

function LessonCard({ lesson, isLocked, isLockedByProgression, onSelect, progressCompleted, progressTotal }: LessonCardProps) {
  const { t } = useTranslation();
  const isComplete = progressCompleted === progressTotal && progressTotal > 0;
  const isDisabled = isLocked || isLockedByProgression;
  
  return (
    <Card 
      className={`cursor-pointer transition-all hover:shadow-md ${isDisabled ? 'opacity-60' : ''} ${isComplete ? 'border-green-500/50' : ''}`}
      onClick={onSelect}
    >
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            {isComplete ? (
              <Check className="h-4 w-4 text-green-600" />
            ) : isDisabled ? (
              <Lock className="h-4 w-4 text-muted-foreground" />
            ) : (
              <BookOpen className="h-4 w-4 text-primary" />
            )}
            {lesson.title}
          </CardTitle>
          <div className="flex items-center gap-2">
            {lesson.isFree && <Badge variant="secondary">{t('latexCenter.lessons.free', 'Free')}</Badge>}
            {isLockedByProgression && !isLocked && (
              <Badge variant="outline" className="text-xs">
                {t('latexCenter.lessons.completePreviousShort', 'Complete previous')}
              </Badge>
            )}
            {isComplete && (
              <Badge className="bg-green-600 hover:bg-green-700">
                <Check className="h-3 w-3 mr-1" />
                {t('latexCenter.progress.complete', 'Complete')}
              </Badge>
            )}
          </div>
        </div>
        <CardDescription className="text-sm">{lesson.description}</CardDescription>
        {progressTotal > 0 && progressCompleted > 0 && !isComplete && (
          <ProgressIndicator 
            completed={progressCompleted} 
            total={progressTotal} 
            size="sm" 
            showLabel={false}
            className="mt-2"
          />
        )}
      </CardHeader>
    </Card>
  );
}

interface LessonContentProps {
  lesson: Lesson;
  onBack: () => void;
  isExerciseCompleted: (lessonId: string, exerciseId: string) => boolean;
  onExerciseComplete: (lessonId: string, exerciseId: string, attempts: number) => void;
}

function LessonContent({ lesson, onBack, isExerciseCompleted, onExerciseComplete }: LessonContentProps) {
  const { t } = useTranslation();
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null);
  const completedCount = lesson.exercises.filter((_, i) => isExerciseCompleted(lesson.id, `ex_${i}`)).length;

  const copyLatex = (latex: string, index: number) => {
    navigator.clipboard.writeText(latex);
    setCopiedIndex(index);
    toast.success("LaTeX copied to clipboard");
    setTimeout(() => setCopiedIndex(null), 2000);
  };

  return (
    <div className="space-y-6">
      <Button variant="ghost" onClick={onBack} className="mb-4">
        ← {t('latexCenter.lessons.backToLessons', 'Back to lessons')}
      </Button>
      
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold mb-2">{lesson.title}</h2>
          <p className="text-muted-foreground whitespace-pre-line">{lesson.content}</p>
        </div>
        {lesson.exercises.length > 0 && (
          <div className="shrink-0 w-32">
            <ProgressIndicator
              completed={completedCount}
              total={lesson.exercises.length}
              size="md"
            />
          </div>
        )}
      </div>

      <div className="space-y-4">
        <h3 className="text-lg font-semibold flex items-center gap-2">
          <Code2 className="h-5 w-5" />
          {t('latexCenter.lessons.examples', 'Examples')}
        </h3>
        <div className="grid gap-4">
          {lesson.examples.map((example, index) => (
            <Card key={index} className="overflow-hidden">
              <div className="grid md:grid-cols-2">
                <div className="p-4 bg-muted/30 border-b md:border-b-0 md:border-r">
                  <div className="flex items-start justify-between gap-2">
                    <code className="text-sm font-mono break-all">{example.latex}</code>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6 shrink-0"
                      onClick={() => copyLatex(example.latex, index)}
                    >
                      {copiedIndex === index ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">{example.description}</p>
                </div>
                <div className="p-4 flex items-center justify-center bg-slate-100 dark:bg-slate-200 min-h-[60px]">
                  <Math block>{example.latex}</Math>
                </div>
              </div>
            </Card>
          ))}
        </div>
      </div>

      {lesson.exercises.length > 0 && (
        <div className="space-y-4">
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <FileText className="h-5 w-5" />
            {t('latexCenter.lessons.practiceExercises', 'Practice Exercises')}
          </h3>
          <div className="grid gap-4">
            {lesson.exercises.map((exercise, index) => (
              <InteractiveExercise
                key={index}
                exerciseNumber={index + 1}
                prompt={exercise.prompt}
                answer={exercise.answer}
                lessonId={lesson.id}
                exerciseId={`ex_${index}`}
                isCompleted={isExerciseCompleted(lesson.id, `ex_${index}`)}
                onComplete={(attempts) => onExerciseComplete(lesson.id, `ex_${index}`, attempts)}
              />
            ))}
          </div>
        </div>
      )}

      <Card className="bg-primary/5 border-primary/20">
        <CardContent className="pt-4">
          <div className="flex items-start gap-3">
            <Sparkles className="h-5 w-5 text-primary shrink-0 mt-0.5" />
            <div>
              <p className="font-medium">{t('latexCenter.lessons.practiceInFormatter', 'Practice in the LaTeX Formatter')}</p>
              <p className="text-sm text-muted-foreground mb-3">
                {t('latexCenter.lessons.formatterDescription', 'Try these examples in our live editor with instant preview and export options.')}
              </p>
              <Button variant="outline" size="sm" asChild>
                <a href="/calculator/latex-formatter">{t('latexCenter.lessons.openFormatter', 'Open LaTeX Formatter')} →</a>
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default function LaTeXLearningCenter() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { hasAccess, isLoading } = useUserRole();
  const { t } = useTranslation();
  const [selectedLesson, setSelectedLesson] = useState<Lesson | null>(null);
  const [activeTab, setActiveTab] = useState("beginner");
  
  const { isExerciseCompleted, getLessonProgress, markCompleted } = useLatexProgress();

  const isMember = hasAccess(["admin", "paid", "student"]);

  // Helper to check if all lessons in a module are complete
  const isModuleComplete = (lessons: Lesson[]) => {
    return lessons.every(lesson => {
      const progress = getLessonProgress(lesson.id, lesson.exercises.length);
      return progress.completed === progress.total && progress.total > 0;
    });
  };

  // Helper to check if a specific lesson is complete
  const isLessonComplete = (lesson: Lesson) => {
    const progress = getLessonProgress(lesson.id, lesson.exercises.length);
    return progress.completed === progress.total && progress.total > 0;
  };

  // Helper to check if previous lesson in a list is complete (for sequential locking)
  const isPreviousLessonComplete = (lessons: Lesson[], currentIndex: number) => {
    if (currentIndex === 0) return true; // First lesson is always unlocked
    return isLessonComplete(lessons[currentIndex - 1]);
  };

  // Check if all prerequisites are complete for intermediate (all beginner lessons)
  const canAccessIntermediate = isModuleComplete(beginnerLessons);
  
  // Check if all prerequisites are complete for advanced (all intermediate lessons)
  const canAccessAdvanced = canAccessIntermediate && isModuleComplete(intermediateLessons);
  
  // Check if all prerequisites are complete for final project (all lessons)
  const canAccessFinalProject = canAccessAdvanced && isModuleComplete(advancedLessons);

  const handleSelectLesson = (lesson: Lesson, requiresMembership: boolean, isLockedByProgression: boolean) => {
    if (requiresMembership && !isMember) {
      toast.error(t('latexCenter.lessons.membershipRequired') || "This lesson requires a membership");
      return;
    }
    if (isLockedByProgression) {
      toast.error(t('latexCenter.lessons.completePrevious') || "Complete the previous lesson first");
      return;
    }
    setSelectedLesson(lesson);
  };

  const handleExerciseComplete = async (lessonId: string, exerciseId: string, attempts: number) => {
    if (!user) {
      toast.info(t('latexCenter.signInToSave', 'Sign in to save your progress'));
      return;
    }
    await markCompleted(lessonId, exerciseId, attempts);
    toast.success(t('latexCenter.exercises.completed', 'Completed') + '!');
  };

  if (selectedLesson) {
    return (
      <>
        <SEO
          title={`${selectedLesson.title} - LaTeX Learning Center | CalcLab`}
          description="Learn LaTeX for professional mathematical formatting"
        />
        <PageLayout>
          <div className="container max-w-4xl py-8 px-4">
            <LessonContent 
              lesson={selectedLesson} 
              onBack={() => setSelectedLesson(null)}
              isExerciseCompleted={isExerciseCompleted}
              onExerciseComplete={handleExerciseComplete}
            />
          </div>
        </PageLayout>
      </>
    );
  }

  return (
    <>
      <SEO
        title="LaTeX Learning Center | CalcLab"
        description="Master LaTeX for professional mathematical formatting. Learn to write equations, edit SuperCalc outputs, and create publication-ready documents."
      />
      <PageLayout>
        <div className="container max-w-4xl py-8 px-4 space-y-8">
        {/* Hero Section */}
        <div className="text-center space-y-4">
          <div className="flex items-center justify-center gap-2 text-primary">
            <GraduationCap className="h-8 w-8" />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold">{t('latexCenter.title')}</h1>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            {t('latexCenter.subtitle')}
          </p>
          {!user && (
            <Button onClick={() => navigate("/auth")}>
              {t('latexCenter.signInProgress')}
            </Button>
          )}
        </div>

        {/* Membership CTA for non-members */}
        {!isMember && !isLoading && (
          <Card className="bg-gradient-to-r from-primary/10 to-accent/10 border-primary/20">
            <CardContent className="pt-6">
              <div className="flex flex-col md:flex-row items-center gap-4">
                <div className="flex-1">
                  <h3 className="font-semibold text-lg flex items-center gap-2">
                    <Lock className="h-5 w-5" />
                    {t('latexCenter.unlockTitle')}
                  </h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    {t('latexCenter.unlockDescription')}
                  </p>
                </div>
                <Button onClick={() => navigate("/membership")}>
                  {t('home.latexLearning.viewPlans')}
                  <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Lessons Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="beginner" className="gap-1">
              <BookOpen className="h-4 w-4 hidden sm:inline" />
              {t('latexCenter.beginner')}
            </TabsTrigger>
            <TabsTrigger value="intermediate" className="gap-1">
              <Code2 className="h-4 w-4 hidden sm:inline" />
              {t('latexCenter.intermediate')}
              {!isMember && <Lock className="h-3 w-3 ml-1" />}
            </TabsTrigger>
            <TabsTrigger value="advanced" className="gap-1">
              <Sparkles className="h-4 w-4 hidden sm:inline" />
              {t('latexCenter.advanced')}
              {!isMember && <Lock className="h-3 w-3 ml-1" />}
            </TabsTrigger>
            <TabsTrigger value="project" className="gap-1">
              <Trophy className="h-4 w-4 hidden sm:inline" />
              {t('latexCenter.finalProject', 'Project')}
              {!isMember && <Lock className="h-3 w-3 ml-1" />}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="beginner" className="mt-6">
            <div className="grid gap-4">
              {beginnerLessons.map((lesson, index) => {
                const progress = getLessonProgress(lesson.id, lesson.exercises.length);
                const isLockedByProgression = !isPreviousLessonComplete(beginnerLessons, index);
                return (
                  <LessonCard
                    key={lesson.id}
                    lesson={lesson}
                    isLocked={false}
                    isLockedByProgression={isLockedByProgression}
                    onSelect={() => handleSelectLesson(lesson, false, isLockedByProgression)}
                    progressCompleted={progress.completed}
                    progressTotal={progress.total}
                  />
                );
              })}
            </div>
          </TabsContent>

          <TabsContent value="intermediate" className="mt-6">
            {!canAccessIntermediate && isMember && (
              <Card className="mb-4 bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-800">
                <CardContent className="pt-4">
                  <p className="text-sm text-amber-800 dark:text-amber-200 flex items-center gap-2">
                    <Lock className="h-4 w-4" />
                    {t('latexCenter.lessons.completeBeginnerFirst', 'Complete all Beginner lessons to unlock Intermediate')}
                  </p>
                </CardContent>
              </Card>
            )}
            <div className="grid gap-4">
              {intermediateLessons.map((lesson, index) => {
                const progress = getLessonProgress(lesson.id, lesson.exercises.length);
                const isLockedByProgression = !canAccessIntermediate || !isPreviousLessonComplete(intermediateLessons, index);
                return (
                  <LessonCard
                    key={lesson.id}
                    lesson={lesson}
                    isLocked={!isMember}
                    isLockedByProgression={isMember ? isLockedByProgression : false}
                    onSelect={() => handleSelectLesson(lesson, true, isMember ? isLockedByProgression : false)}
                    progressCompleted={progress.completed}
                    progressTotal={progress.total}
                  />
                );
              })}
            </div>
            {!isMember && (
              <Card className="mt-4 bg-muted/50">
                <CardContent className="pt-4 text-center">
                  <p className="text-sm text-muted-foreground mb-3">
                    {t('latexCenter.upgradePrompt')}
                  </p>
                  <Button variant="outline" size="sm" onClick={() => navigate("/membership")}>
                    {t('home.latexLearning.viewPlans')}
                  </Button>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="advanced" className="mt-6">
            {!canAccessAdvanced && isMember && (
              <Card className="mb-4 bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-800">
                <CardContent className="pt-4">
                  <p className="text-sm text-amber-800 dark:text-amber-200 flex items-center gap-2">
                    <Lock className="h-4 w-4" />
                    {t('latexCenter.lessons.completeIntermediateFirst', 'Complete all Intermediate lessons to unlock Advanced')}
                  </p>
                </CardContent>
              </Card>
            )}
            <div className="grid gap-4">
              {advancedLessons.map((lesson, index) => {
                const progress = getLessonProgress(lesson.id, lesson.exercises.length);
                const isLockedByProgression = !canAccessAdvanced || !isPreviousLessonComplete(advancedLessons, index);
                return (
                  <LessonCard
                    key={lesson.id}
                    lesson={lesson}
                    isLocked={!isMember}
                    isLockedByProgression={isMember ? isLockedByProgression : false}
                    onSelect={() => handleSelectLesson(lesson, true, isMember ? isLockedByProgression : false)}
                    progressCompleted={progress.completed}
                    progressTotal={progress.total}
                  />
                );
              })}
            </div>
            {!isMember && (
              <Card className="mt-4 bg-muted/50">
                <CardContent className="pt-4 text-center">
                  <p className="text-sm text-muted-foreground mb-3">
                    {t('latexCenter.upgradePrompt')}
                  </p>
                  <Button variant="outline" size="sm" onClick={() => navigate("/membership")}>
                    {t('home.latexLearning.viewPlans')}
                  </Button>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="project" className="mt-6">
            {!isMember ? (
              <Card className="bg-muted/50">
                <CardContent className="pt-6 text-center">
                  <Trophy className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                  <h3 className="font-semibold text-lg mb-2">{t('latexCenter.finalProject.locked', 'Final Project')}</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    {t('latexCenter.finalProject.lockedDesc', 'Complete a professional LaTeX report assessed by AI. Upgrade to access the final project.')}
                  </p>
                  <Button onClick={() => navigate("/membership")}>
                    {t('home.latexLearning.viewPlans')}
                  </Button>
                </CardContent>
              </Card>
            ) : !canAccessFinalProject ? (
              <Card className="bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-800">
                <CardContent className="pt-6 text-center">
                  <Lock className="h-12 w-12 mx-auto mb-4 text-amber-600 dark:text-amber-400" />
                  <h3 className="font-semibold text-lg mb-2">{t('latexCenter.finalProject.locked', 'Final Project')}</h3>
                  <p className="text-sm text-amber-800 dark:text-amber-200 mb-4">
                    {t('latexCenter.finalProject.completeAllFirst', 'Complete all Beginner, Intermediate, and Advanced lessons to unlock the Final Project')}
                  </p>
                  <div className="flex flex-wrap justify-center gap-2 text-xs">
                    <Badge variant={isModuleComplete(beginnerLessons) ? "default" : "outline"} className={isModuleComplete(beginnerLessons) ? "bg-green-600" : ""}>
                      {isModuleComplete(beginnerLessons) ? <Check className="h-3 w-3 mr-1" /> : null}
                      Beginner
                    </Badge>
                    <Badge variant={isModuleComplete(intermediateLessons) ? "default" : "outline"} className={isModuleComplete(intermediateLessons) ? "bg-green-600" : ""}>
                      {isModuleComplete(intermediateLessons) ? <Check className="h-3 w-3 mr-1" /> : null}
                      Intermediate
                    </Badge>
                    <Badge variant={isModuleComplete(advancedLessons) ? "default" : "outline"} className={isModuleComplete(advancedLessons) ? "bg-green-600" : ""}>
                      {isModuleComplete(advancedLessons) ? <Check className="h-3 w-3 mr-1" /> : null}
                      Advanced
                    </Badge>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <FinalProject />
            )}
          </TabsContent>
        </Tabs>

        {/* Quick Reference */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">{t('latexCenter.quickReference')}</CardTitle>
            <CardDescription>{t('latexCenter.quickReferenceDesc')}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div>
                <p className="font-medium mb-1">{t('latexCenter.fractions')}</p>
                <code className="text-xs text-muted-foreground">\frac{"{a}"}{"{b}"}</code>
              </div>
              <div>
                <p className="font-medium mb-1">{t('latexCenter.squareRoot')}</p>
                <code className="text-xs text-muted-foreground">\sqrt{"{x}"}</code>
              </div>
              <div>
                <p className="font-medium mb-1">{t('latexCenter.superscript')}</p>
                <code className="text-xs text-muted-foreground">x^2</code>
              </div>
              <div>
                <p className="font-medium mb-1">{t('latexCenter.subscript')}</p>
                <code className="text-xs text-muted-foreground">x_i</code>
              </div>
              <div>
                <p className="font-medium mb-1">{t('latexCenter.greek')}</p>
                <code className="text-xs text-muted-foreground">\alpha, \beta</code>
              </div>
              <div>
                <p className="font-medium mb-1">{t('latexCenter.sum')}</p>
                <code className="text-xs text-muted-foreground">\sum_{"{i=1}"}^n</code>
              </div>
              <div>
                <p className="font-medium mb-1">{t('latexCenter.integral')}</p>
                <code className="text-xs text-muted-foreground">\int_a^b</code>
              </div>
              <div>
                <p className="font-medium mb-1">{t('latexCenter.infinity')}</p>
                <code className="text-xs text-muted-foreground">\infty</code>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      </PageLayout>
    </>
  );
}
